import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate, today } from "@/lib/format";
import { PageHeader, Panel, Empty } from "@/components/Shell";

const emptyLine = () => ({ account_id: "", description: "", debit: 0, credit: 0 });

export default function JournalEntries() {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: today(), memo: "", reference: "", lines: [emptyLine(), emptyLine()] });

  const load = () => api.get("/journal-entries").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/accounts").then(({ data }) => setAccounts(data));
  }, []);

  const totalDebit = form.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.05 && totalDebit > 0;

  const setLine = (idx, patch) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setForm({ ...form, lines });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!balanced) return toast.error("Debits must equal credits and be greater than zero");
    try {
      await api.post("/journal-entries", {
        date: form.date, memo: form.memo, reference: form.reference,
        lines: form.lines.filter((l) => l.account_id).map((l) => ({
          account_id: l.account_id, description: l.description,
          debit: Number(l.debit || 0), credit: Number(l.credit || 0),
        })),
      });
      toast.success("Journal entry posted");
      setOpen(false);
      setForm({ date: today(), memo: "", reference: "", lines: [emptyLine(), emptyLine()] });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/journal-entries/${id}`); toast.success("Entry deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="journal-page">
      <PageHeader title="Journal Entries" subtitle="Every posting in your books — automatic and manual double-entry."
        action={canWrite && (
          <button data-testid="add-journal-button" onClick={() => setOpen(true)}
            className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
            <Plus size={16} /> New entry
          </button>
        )} />

      <div className="space-y-4">
        {rows.length ? rows.map((e) => (
          <Panel key={e.id} title={`${shortDate(e.date)} · ${e.memo}`}
            right={
              <div className="flex items-center gap-3">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold capitalize text-slate-600">{e.source}</span>
                {canWrite && e.source === "manual" && (
                  <button data-testid="delete-journal-button" onClick={() => remove(e.id)} className="text-slate-400 hover:text-rose-600" aria-label="Delete entry">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            }>
            <table className="w-full text-sm" data-testid="journal-entry-table">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-5 py-2">Account</th><th className="px-5 py-2">Description</th>
                  <th className="px-5 py-2 text-right">Debit</th><th className="px-5 py-2 text-right">Credit</th></tr>
              </thead>
              <tbody>
                {e.lines.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-2 font-medium text-slate-800">{l.account_name}</td>
                    <td className="px-5 py-2 text-slate-600">{l.description}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{l.debit ? money(l.debit) : "—"}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{l.credit ? money(l.credit) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        )) : <Panel><Empty message="No journal entries yet." /></Panel>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 p-4">
          <div className="mx-auto w-full max-w-3xl rounded-lg border border-slate-200 bg-white shadow-md" data-testid="journal-dialog">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <h3 className="font-display text-base font-bold text-slate-900">New journal entry</h3>
              <button data-testid="close-journal-dialog" onClick={() => setOpen(false)} className="text-slate-400"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Date</label>
                  <input data-testid="journal-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Memo</label>
                  <input data-testid="journal-memo-input" required value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </div>
              </div>

              <div className="mt-5 overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="px-3 py-2">Account</th><th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 w-32">Debit</th><th className="px-3 py-2 w-32">Credit</th><th /></tr>
                  </thead>
                  <tbody>
                    {form.lines.map((l, idx) => (
                      <tr key={idx} className="border-t border-slate-100">
                        <td className="px-3 py-2">
                          <select data-testid={`journal-account-select-${idx}`} value={l.account_id} onChange={(e) => setLine(idx, { account_id: e.target.value })}
                            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm">
                            <option value="">Select account…</option>
                            {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input data-testid={`journal-description-input-${idx}`} value={l.description} onChange={(e) => setLine(idx, { description: e.target.value })}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input data-testid={`journal-debit-input-${idx}`} type="number" step="any" value={l.debit} onChange={(e) => setLine(idx, { debit: e.target.value, credit: 0 })}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                        </td>
                        <td className="px-3 py-2">
                          <input data-testid={`journal-credit-input-${idx}`} type="number" step="any" value={l.credit} onChange={(e) => setLine(idx, { credit: e.target.value, debit: 0 })}
                            className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
                        </td>
                        <td className="px-2">
                          {form.lines.length > 2 && (
                            <button type="button" data-testid={`remove-journal-line-${idx}`} onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })}
                              className="text-slate-400 hover:text-rose-600" aria-label="Remove line"><Trash2 size={14} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 font-semibold">
                    <tr><td className="px-3 py-2" colSpan={2}>Totals</td>
                      <td className="px-3 py-2 tabular-nums" data-testid="journal-total-debit">{money(totalDebit)}</td>
                      <td className="px-3 py-2 tabular-nums" data-testid="journal-total-credit">{money(totalCredit)}</td><td /></tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <button type="button" data-testid="add-journal-line-button" onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}
                  className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline"><Plus size={14} /> Add line</button>
                <span data-testid="journal-balance-indicator" className={`text-sm font-semibold ${balanced ? "text-emerald-700" : "text-rose-600"}`}>
                  {balanced ? "Balanced" : `Out of balance by ${money(Math.abs(totalDebit - totalCredit))}`}
                </span>
              </div>

              <button data-testid="save-journal-button" disabled={!balanced}
                className="mt-5 w-full rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                Post entry
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
